import React, { useContext, useState } from 'react';
import * as R from 'ramda';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AUTH_TYPE as authorizationTypes,
  getSupplierMainAuthTypes,
  getSupplierProductAuthTypes,
} from 'feature/loading-management/utils/const';
import { arrayOf, bool, number, oneOfType, string } from 'prop-types';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { useUserRolePermissions } from 'hooks';
import {
  CreateCustomerAuthContext,
  initialStateStep2,
  initialStateStep3,
  initialStateStep4,
} from '../Create';

const AuthorizationVolumes = ({ dataPath, isProduct }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const { info, setInfo } = useContext(CreateCustomerAuthContext);

  const [validation, setValidation] = useState({
    authorizationVolume: '',
    renewalVolume: '',
  });

  const dataLens = R.lensPath(dataPath);
  const authorizationTypeLens = R.o(dataLens, R.lensProp('authorizationType'));
  const authorizationVolumeLens = R.o(dataLens, R.lensProp('authorizationVolume'));
  const renewalVolumeLens = R.o(dataLens, R.lensProp('renewalVolume'));

  const authorizationType = R.view(authorizationTypeLens, info);
  const authorizationVolume = R.view(authorizationVolumeLens, info);
  const renewalVolume = R.view(renewalVolumeLens, info);

  const { isSupplier } = useUserRolePermissions();

  const originalFilteredTypes = isProduct
    ? authorizationTypes.filter(
        ({ value }) =>
          value !== 'SPECIFIC' &&
          value !== 'ONETIME' &&
          value !== '1VOLDAILY' &&
          value !== '1VOLWEEKLY' &&
          value !== '1VOLMONTH'
      )
    : authorizationTypes.filter(({ value }) => value !== 'ONEVOL');

  // Derive the dropdown options based on role, product‑mode, and flag
  let authTypes;
  if (isSupplier) {
    authTypes = isProduct
      ? getSupplierProductAuthTypes().filter(({ value }) =>
          originalFilteredTypes.some(({ value: filteredValue }) => filteredValue === value)
        )
      : getSupplierMainAuthTypes().filter(({ value }) =>
          originalFilteredTypes.some(({ value: filteredValue }) => filteredValue === value)
        );
  } else {
    authTypes = originalFilteredTypes;
  }

  const resetState = R.equals('products', R.head(dataPath))
    ? R.mergeLeft(initialStateStep4, initialStateStep3)
    : R.mergeAll([initialStateStep4, initialStateStep3, initialStateStep2]);

  // prettier-ignore
  // updateAuthorizationType :: authorizationType -> info -> info
  const updateAuthorizationType = R.curryN(2, R.compose(
    R.ifElse(
      R.propEq('authorizationType', 'ONELOAD'),
      R.assoc('authorizationVolume', 1),
      R.assoc('authorizationVolume', undefined)
    ),
    R.assoc('renewalVolume', undefined),
    R.assoc('authorizationType')
  ));

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  // prettier-ignore
  // handleAuthorizationTypeChange :: AuthorizationType -> info
  const handleAuthorizationTypeChange = R.compose(
    setInfo,
    R.curryN(2, R.compose(
      R.mergeLeft(resetState),
      R.over(dataLens),
    )),
    updateAuthorizationType,
    R.path(['target', 'value'])
  );

  // prettier-ignore
  // handleAuthorizationVolumeChange :: event -> info
  const handleAuthorizationVolumeChange = R.compose(
    setInfo,
    R.curryN(2, R.compose(
      R.mergeLeft(resetState),
      R.over(dataLens)
    )),
    R.assoc('authorizationVolume'),
    R.tap(R.compose(
      setValidation, 
      R.assoc('authorizationVolume'), 
      validateVolume
    )),
    parseInt,
    R.path(['target', 'value'])
  );

  // prettier-ignore
  // handleRenewalVolumeChange :: event -> info
  const handleRenewalVolumeChange = R.compose(
    setInfo,
    R.curryN(2, R.compose(
      R.mergeLeft(resetState),
      R.over(dataLens),
    )),
    R.assoc('renewalVolume'),
    R.tap(R.compose(
      setValidation, 
      R.assoc('renewalVolume'), 
      validateVolume
    )),
    parseInt,
    R.path(['target', 'value'])
  );

  return (
    <Stack direction={lgUp ? 'row' : 'column'} spacing={2}>
      <FormControl required sx={{ flexBasis: 0, flexGrow: 1 }}>
        <InputLabel id="authorization-type">Authorization Type</InputLabel>
        <Select
          label="Authorization Type"
          labelId="authorization-type"
          onChange={handleAuthorizationTypeChange}
          value={authorizationType}
        >
          {authTypes.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        disabled={['ONELOAD', 'UNLIMITED', 'SPECIFIC'].includes(authorizationType)}
        error={isNotEmpty(validation.authorizationVolume)}
        helperText={validation.authorizationVolume || 'Volume (gal)'}
        label="Authorization"
        onChange={handleAuthorizationVolumeChange}
        placeholder="--"
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', authorizationVolume)}
      />

      <TextField
        disabled={['LIMITED', 'ONELOAD', 'ONETIME', 'UNLIMITED', 'SPECIFIC'].includes(
          authorizationType
        )}
        error={isNotEmpty(validation.renewalVolume)}
        helperText={validation.renewalVolume || 'Volume (gal)'}
        label="Renewal"
        onChange={handleRenewalVolumeChange}
        placeholder="--"
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', renewalVolume)}
      />
    </Stack>
  );
};

AuthorizationVolumes.propTypes = {
  dataPath: arrayOf(oneOfType([number, string])).isRequired,
  isProduct: bool,
};

AuthorizationVolumes.defaultProps = {
  isProduct: false,
};

export default AuthorizationVolumes;
